import React, {useContext, useEffect, useState} from "react";
import {Link} from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import {
    ArrowUturnLeftIcon,
    CheckCircleIcon,
    EyeIcon,
    XCircleIcon,
} from "@heroicons/react/24/outline";
import {formatStatus, getStatusBadgeClass, truncateText} from "../utils/Utils";
import {AuthContext} from "../context/AuthContext";
import {
    EmptyState,
    Page,
    PageHeader,
    PageLoader,
    Panel,
    SearchInput,
    SectionHeading,
    StatTile,
    TableShell,
    Td,
    TdEmpty,
    Th,
    Tr,
} from "../components/ui";

const Dashboard = () => {
    const {user, updateUser, updateUserLocal} = useContext(AuthContext);
    const [dashboard, setDashboard] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchFiles = async () => {
            try {
                const response = await axiosInstance.get(`/dashboard/${user.id}`);
                setDashboard(response.data);
            } finally {
                setLoading(false);
            }
        };

        fetchFiles();

        const fetchUserData = async () => {
            try {
                const response = await axiosInstance.get("/users/my");
                const updatedUser = {
                    ...user,
                    credit_balance: response.data.credit_balance,
                    ai_credit_balance: response.data.ai_credit_balance,
                };
                updateUser(updatedUser);
                updateUserLocal();
            } finally {
                // Optionally handle any cleanup or final logic here
            }
        };
        fetchUserData();
    }, []);

    const filteredFiles =
        dashboard &&
        dashboard.files.filter(
            (file) =>
                file.file_id
                    .toString()
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                file.file_name.toLowerCase().includes(searchTerm.toLowerCase())
        );

    return (
        <Page>
            <PageHeader title="Dashboard" subtitle={`Welcome back, ${user?.username || "there"}.`}/>

            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                <StatTile
                    label="Processed"
                    value={loading ? "…" : dashboard?.completed ?? 0}
                    tone="success"
                    icon={<CheckCircleIcon className="h-6 w-6"/>}
                />
                <StatTile
                    label="Refunded"
                    value={loading ? "…" : dashboard?.refunded ?? 0}
                    tone="info"
                    icon={<ArrowUturnLeftIcon className="h-6 w-6"/>}
                />
                <StatTile
                    label="Canceled"
                    value={loading ? "…" : dashboard?.canceled ?? 0}
                    tone="danger"
                    icon={<XCircleIcon className="h-6 w-6"/>}
                />
            </div>

            {loading ? (
                <PageLoader label="Loading your dashboard…"/>
            ) : (
                <>
                    <SearchInput
                        placeholder="Search by file id, file name…"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />

                    <div className="grid gap-5 xl:grid-cols-2">
                        <Panel>
                            <SectionHeading title="Last Files"/>
                            <TableShell className="mt-4" maxHeight="max-h-[520px]">
                                <thead>
                                <tr>
                                    <Th>File ID</Th>
                                    <Th>File Name</Th>
                                    <Th>Price Status</Th>
                                    <Th>Status</Th>
                                    <Th align="center">View</Th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredFiles && filteredFiles.length > 0 ? (
                                    filteredFiles.map((file) => (
                                        <Tr key={file.id}>
                                            <Td className="font-mono text-xs">{file.file_id}</Td>
                                            <Td className="font-medium text-gray-800 dark:text-slate-200">
                                                {truncateText(file.file_name, 16)}
                                            </Td>
                                            <Td>
                          <span className={getStatusBadgeClass(file.price_status)}>
                            {formatStatus(file.price_status)}
                          </span>
                                            </Td>
                                            <Td>
                          <span className={getStatusBadgeClass(file.status)}>
                            {formatStatus(file.status)}
                          </span>
                                            </Td>
                                            <Td className="text-center">
                                                <Link
                                                    to={`/view-file/${file.id}`}
                                                    title="View file"
                                                    className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-red-500/10 hover:text-red-500 dark:hover:text-orange-400"
                                                >
                                                    <EyeIcon className="h-5 w-5"/>
                                                </Link>
                                            </Td>
                                        </Tr>
                                    ))
                                ) : (
                                    <TdEmpty colSpan={5}>No pending files found.</TdEmpty>
                                )}
                                </tbody>
                            </TableShell>
                        </Panel>

                        <Panel>
                            <SectionHeading title="Last Tickets"/>
                            {dashboard?.tickets && dashboard.tickets.length > 0 ? (
                                <TableShell className="mt-4" maxHeight="max-h-[520px]">
                                    <thead>
                                    <tr>
                                        <Th>Ticket ID</Th>
                                        <Th>Subject</Th>
                                        <Th>Status</Th>
                                        <Th align="center">View</Th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {dashboard.tickets.map((ticket) => (
                                        <Tr key={ticket.id}>
                                            <Td className="font-mono text-xs">{ticket.ticket_id}</Td>
                                            <Td className="font-medium text-gray-800 dark:text-slate-200">
                                                {ticket.subject}
                                            </Td>
                                            <Td>
                          <span className={getStatusBadgeClass(ticket.status)}>
                            {formatStatus(ticket.status)}
                          </span>
                                            </Td>
                                            <Td className="text-center">
                                                <Link
                                                    to={`/view-file/${ticket.file_id}`}
                                                    title="View ticket"
                                                    className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-red-500/10 hover:text-red-500 dark:hover:text-orange-400"
                                                >
                                                    <EyeIcon className="h-5 w-5"/>
                                                </Link>
                                            </Td>
                                        </Tr>
                                    ))}
                                    </tbody>
                                </TableShell>
                            ) : (
                                <EmptyState
                                    className="mt-4"
                                    title="No open tickets"
                                    hint="Tickets you open about a file appear here."
                                />
                            )}
                        </Panel>
                    </div>
                </>
            )}
        </Page>
    );
};

export default Dashboard;
